import { Box, colors, Divider, Typography, useTheme } from "@mui/material";
import Section from "components/Section";
import OutlinedCard from "components/OutlinedCard";
import { Checklist } from "models/Checklist";
import { useState } from "react";
import EditRoomName from "./edit-components/EditRoomName";
import { useAppSelector } from "app/hooks";
import { selectUserRole } from "features/auth/authSlice";
import { UserRole } from "features/auth/types/user";
import DocumentationStatusModal from "./DocumentationStatusModal";
import FunctionCheckModal from "./edit-components/FunctionCheckModal";
import StatusDisplay from "./StatusDisplay";
import UserActivity from "components/UserActivity";
import CategoriesSection from "./CategoriesSection";

type ChecklistDataProps = {
  checklist: Checklist;
};

const ChecklistData: React.FC<ChecklistDataProps> = ({ checklist }) => {
  const theme = useTheme();
  const userRole = useAppSelector(selectUserRole);
  const [showDocumentationStatusModal, setShowDocumentationStatusModal] = useState(false);
  const [showFunctionCheckModal, setShowFunctionCheckModal] = useState(false);

  const isCompleted = checklist.documentationDone && checklist.functionCheck;
  const isAdmin = userRole === UserRole.ADMIN || userRole === UserRole.USER_ADMIN;
  const isSales = userRole === UserRole.SALES;

  return (
    <Section mb={1}>
      <OutlinedCard sx={{ display: "grid", alignItems: "center", p: 0, overflow: "hidden" }}>
        <Box
          sx={{
            p: 2,
            pl: 3,
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            position: "relative",
            background: isCompleted
              ? `linear-gradient(135deg, transparent, ${colors.green[100]})`
              : `linear-gradient(135deg, ${colors.grey[100]}, ${colors.grey[200]})`,
            "::before": {
              content: "''",
              position: "absolute",
              left: 0,
              insetBlock: "0",
              width: "0.5rem",
              backgroundColor: isCompleted ? theme.palette.success.main : theme.palette.grey[600],
            },
          }}
        >
          <Box display="flex" flexDirection="column">
            <Typography fontWeight="bold" fontSize="1.5rem">
              {checklist.partOfOrder}
            </Typography>

            <Box display="flex" alignItems="center">
              <Typography fontWeight="bold" fontSize="2rem">
                {checklist.roomName}
              </Typography>

              {(isAdmin || isSales) && <EditRoomName />}
            </Box>

            <Typography fontWeight="bold">Skapad</Typography>
            <UserActivity
              time={checklist.createdAt}
              user={{ userName: checklist.creator.userName, userId: checklist.creator.id }}
            />
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplate: "repeat(2, auto) / repeat(3, 1fr)",
              columnGap: "2rem",
              alignItems: "start",
              justifyItems: "center",
              textAlign: "center",
              gridAutoFlow: "column",
            }}
          >
            <StatusDisplay
              status={checklist.documentationDone}
              title="Dokumentation"
              showEdit={isAdmin}
              onEdit={() => setShowDocumentationStatusModal(true)}
              editTooltip="Ändra status för dokumentation"
            >
              {checklist.documentationDone &&
              checklist.documentationDoneAt &&
              checklist.documentationDoneBy ? (
                <UserActivity
                  time={checklist.documentationDoneAt}
                  user={checklist.documentationDoneBy}
                />
              ) : (
                <Typography fontSize="small">Ej klar</Typography>
              )}
            </StatusDisplay>

            <StatusDisplay
              status={checklist.functionCheck}
              title="Funktionskontroll"
              showEdit={isAdmin}
              onEdit={() => setShowFunctionCheckModal(true)}
              editTooltip="Ändra status för funktionskontroll"
            >
              {checklist.functionCheck && checklist.functionCheckAt && checklist.functionCheckBy ? (
                <>
                  <UserActivity time={checklist.functionCheckAt} user={checklist.functionCheckBy} />
                  {checklist.functionCheckByAdmin && (
                    <Typography fontSize="small">Ändrad av admin</Typography>
                  )}
                </>
              ) : (
                <Typography fontSize="small">Ej utförd</Typography>
              )}
            </StatusDisplay>

            <StatusDisplay status={checklist.approved} title="Besiktning">
              <Typography fontSize="small">
                {checklist.approved ? "Godkänd av kund" : "Ej godkänd av kund"}
              </Typography>
            </StatusDisplay>
          </Box>
        </Box>

        <Divider color="grey" />

        <CategoriesSection checklist={checklist} />
      </OutlinedCard>

      {showDocumentationStatusModal && (
        <DocumentationStatusModal
          checklist={checklist}
          onClose={() => setShowDocumentationStatusModal(false)}
        />
      )}

      {showFunctionCheckModal && (
        <FunctionCheckModal
          checklist={checklist}
          onClose={() => setShowFunctionCheckModal(false)}
        />
      )}
    </Section>
  );
};

export default ChecklistData;
